import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldAlert, CheckCircle2, Clock, Circle, Calendar, Play } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useNoIndex } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BRAND } from "@/data/site";

export default function ClientPortal() {
  useNoIndex("Client Portal | VG Homes");
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => { api.get(`/portal/${token}/info`).then((r) => setInfo(r.data)).catch(() => setInfo({ invalid: true })); }, [token]);

  const onDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits]; next[i] = val; setDigits(next);
    if (val && i < 3) refs[i + 1].current?.focus();
  };
  const onKey = (i, e) => { if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus(); };

  const verify = async () => {
    const pin = digits.join("");
    if (pin.length !== 4) { setError("Enter all 4 digits"); return; }
    setLoading(true); setError("");
    try {
      const { data: res } = await api.post(`/portal/${token}/verify`, { pin });
      setSession(res.session);
      const tl = await api.get(`/portal/${token}/timeline`, { headers: { Authorization: `Bearer ${res.session}` } });
      setData(tl.data);
      toast.success("Access granted");
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      setDigits(["", "", "", ""]); refs[0].current?.focus();
      if (err?.response?.status === 423) setInfo((p) => ({ ...p, locked: true }));
      else api.get(`/portal/${token}/info`).then((r) => setInfo(r.data)).catch(() => {});
    } finally { setLoading(false); }
  };

  if (info?.invalid) return <Centered><p className="text-white/80">Invalid or expired portal link.</p></Centered>;

  // Timeline view
  if (session && data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-navy py-6">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 flex items-center justify-between">
            <Logo onDark />
            <span className="text-xs text-white/60">Client Progress Portal</span>
          </div>
        </div>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <h1 className="font-display text-3xl font-bold text-slate-900">{data.project?.name}</h1>
          <p className="text-slate-500 mt-1">{data.project?.location}</p>
          {data.project?.status === "Ongoing" && (
            <div className="mt-4 max-w-sm">
              <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Overall Progress</span><span className="font-semibold text-teal-dark">{data.project.progress_percentage}%</span></div>
              <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-teal" style={{ width: `${data.project.progress_percentage}%` }} /></div>
            </div>
          )}

          <div className="relative mt-12 pl-8">
            <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-slate-200" />
            {data.milestones.length === 0 && <p className="text-slate-400">No updates posted yet.</p>}
            {data.milestones.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12, duration: 0.5 }}
                className="relative mb-10" data-testid={`timeline-milestone-${m.id}`}>
                <span className={`absolute -left-8 top-1 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-slate-50 ${
                  m.status === "Completed" ? "bg-teal" : m.status === "In Progress" ? "bg-gold" : "bg-slate-300"}`} />
                <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-slate-400"><Calendar className="h-3.5 w-3.5" /> {m.date}</span>
                    <StatusChip status={m.status} />
                  </div>
                  <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">{m.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{m.description}</p>
                  {m.media?.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {m.media.map((md, k) => (
                        <button key={k} onClick={() => setActive(md)} className="relative overflow-hidden rounded-lg aspect-video bg-slate-100">
                          {md.type === "video" ? (
                            <><video src={md.url} className="h-full w-full object-cover" muted /><span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white"><Play className="h-6 w-6" /></span></>
                          ) : <img src={md.url} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="max-w-3xl p-2">
            {active && (active.type === "video"
              ? <video src={active.url} controls autoPlay className="w-full rounded-lg max-h-[75vh]" />
              : <img src={active.url} alt="" className="w-full rounded-lg max-h-[75vh] object-contain" />)}
          </DialogContent>
        </Dialog>
        <WhatsAppButton />
      </div>
    );
  }

  // PIN entry
  const locked = info?.locked;
  return (
    <Centered>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-2xl">
        <div className="flex justify-center mb-6"><Logo /></div>
        {locked ? (
          <div className="text-center" data-testid="portal-locked-banner">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500 mb-4"><ShieldAlert className="h-8 w-8" /></div>
            <h2 className="font-display text-xl font-bold text-slate-900">Portal Locked</h2>
            <p className="mt-2 text-sm text-slate-500">Too many failed attempts. Please contact VG Homes to unlock your access.</p>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="mt-6 inline-block rounded-full bg-teal px-6 py-3 font-semibold text-white hover:bg-teal-dark transition-colors">Contact to Unlock</a>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-navy/5 text-navy mb-3"><Lock className="h-6 w-6" /></div>
              <h2 className="font-display text-xl font-bold text-slate-900">{info?.project_name || "Client Portal"}</h2>
              <p className="mt-1 text-sm text-slate-500">Enter your 4-digit PIN to view progress</p>
            </div>
            <div className="mt-8 flex justify-center gap-3">
              {digits.map((d, i) => (
                <input key={i} ref={refs[i]} value={d} onChange={(e) => onDigit(i, e.target.value)} onKeyDown={(e) => onKey(i, e)}
                  inputMode="numeric" maxLength={1} data-testid={`client-pin-input-${i}`}
                  className="h-16 w-14 rounded-xl border-2 border-slate-200 text-center font-display text-2xl font-bold text-navy focus:border-teal focus:outline-none transition-colors" />
              ))}
            </div>
            {error && <p className="mt-4 text-center text-sm text-red-500" data-testid="portal-error">{error}</p>}
            {info && !error && <p className="mt-4 text-center text-xs text-slate-400">{info.attempts_left} attempt(s) remaining before lockout</p>}
            <button onClick={verify} disabled={loading} data-testid="portal-verify-button"
              className="mt-6 w-full rounded-full bg-navy py-3.5 font-semibold text-white hover:bg-navy-dark transition-colors disabled:opacity-60">
              {loading ? "Verifying..." : "Unlock Timeline"}
            </button>
          </>
        )}
      </motion.div>
    </Centered>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-navy-void px-4 grain-overlay">{children}</div>;
}

function StatusChip({ status }) {
  const map = {
    "Completed": { i: CheckCircle2, c: "text-teal bg-teal/10" },
    "In Progress": { i: Clock, c: "text-gold bg-gold/10" },
    "Upcoming": { i: Circle, c: "text-slate-400 bg-slate-100" },
  };
  const { i: Icon, c } = map[status] || map["Upcoming"];
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c}`}><Icon className="h-3 w-3" /> {status}</span>;
}
